import { Form, FormItemProps, Progress, Spin } from 'antd';
import { Rule } from 'antd/es/form';
import { RcFile } from 'antd/es/upload';
import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import ReactQuill, { ReactQuillProps } from 'react-quill';
import BlotFormatter from 'quill-blot-formatter';

import clsx from 'clsx';
import { AxiosProgressEvent } from 'axios';
import { useFileUpload } from '../../../hooks/api/files';

import 'react-quill/dist/quill.snow.css';
import './index.scss';

/** Register the module with Quill */
ReactQuill.Quill.register('modules/blotFormatter', BlotFormatter);

/**
 * CHECK ./README.MD FOR GUIDE HOW TO SETUP.
 * */

const defaultRules: Rule[] = [{ max: 40000 }];

interface TableToolbar {
  quill: {
    getModule: (name: string) => {
      insertTable: (rows?: number, columns?: number) => void;
    };
  };
}

function insertArticleTable(this: TableToolbar) {
  this.quill.getModule('articleTable').insertTable(2, 3);
}

const TABLE_ICON = `
<svg viewBox="0 0 18 18">
  <rect class="ql-stroke" height="12" width="12" x="3" y="3"></rect>
  <line class="ql-stroke" x1="3" x2="15" y1="7" y2="7"></line>
  <line class="ql-stroke" x1="3" x2="15" y1="11" y2="11"></line>
  <line class="ql-stroke" x1="7" x2="7" y1="3" y2="15"></line>
  <line class="ql-stroke" x1="11" x2="11" y1="3" y2="15"></line>
</svg>
`;

const FULLSCREEN_ENTER_ICON = `
<svg viewBox="0 0 18 18">
  <polyline class="ql-stroke" points="3 7 3 3 7 3"></polyline>
  <polyline class="ql-stroke" points="11 3 15 3 15 7"></polyline>
  <polyline class="ql-stroke" points="15 11 15 15 11 15"></polyline>
  <polyline class="ql-stroke" points="7 15 3 15 3 11"></polyline>
</svg>
`;

const FULLSCREEN_EXIT_ICON = `
<svg viewBox="0 0 18 18">
  <polyline class="ql-stroke" points="7 3 7 7 3 7"></polyline>
  <polyline class="ql-stroke" points="15 7 11 7 11 3"></polyline>
  <polyline class="ql-stroke" points="11 15 11 11 15 11"></polyline>
  <polyline class="ql-stroke" points="3 11 7 11 7 15"></polyline>
</svg>
`;

/**
 * The tightest px `max-width` on the field or its ancestors (e.g. a form column capped at 800px).
 * Used as the fullscreen width cap, so fullscreen matches the regular layout without freezing
 * the pixel width of the current window.
 */
function getLayoutMaxWidth(element: HTMLElement): number | null {
  let limit = Infinity;

  for (let node: HTMLElement | null = element; node && node !== document.body; node = node.parentElement) {
    const { maxWidth } = getComputedStyle(node);

    if (maxWidth.endsWith('px')) limit = Math.min(limit, Number.parseFloat(maxWidth));
  }

  return Number.isFinite(limit) ? limit : null;
}

interface InputDescriptionProps {
  formItemProps?: FormItemProps;
  inputProps?: ReactQuillProps;
  allowMediaUpload?: boolean;
  disabled?: boolean;
}

function InputRichDescription({
  formItemProps: { rules = [], label = 'Description', ...props } = {},
  inputProps,
  allowMediaUpload = true,
  disabled,
}: InputDescriptionProps) {
  const quillRef = useRef<ReactQuill>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** Width cap of the field in the regular layout, applied in fullscreen. */
  const [layoutMaxWidth, setLayoutMaxWidth] = useState<number | null>(null);

  /** Uploading logic: */
  const [percentCompleted, setPercentCompleted] = useState<number | null>(null);
  const imageController = new AbortController();
  const videoController = new AbortController();

  // eslint-disable-next-line max-len
  const progressHandler = (progressEvent: AxiosProgressEvent) => {
    setPercentCompleted(Math.round((progressEvent.loaded / (progressEvent?.total || 0)) * 100));
  };

  const imageUploader = useFileUpload(progressHandler, imageController, 'admin/files/upload');
  const videoUploader = useFileUpload(progressHandler, videoController, 'admin/files/upload/video');

  /** The handler function for image and video upload */
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');

    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.addEventListener('change', async () => {
      if (input.files && quillRef.current) {
        const file = input.files[0] as RcFile;
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        try {
          const response = await imageUploader.fetchFile(file).finally(() => setPercentCompleted(null));
          const url = response?.location || '';

          editor.insertEmbed(range.index, 'image', url);
          editor.setSelection(range.index + 1, 0);
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      }
    });
  }, [imageUploader]);

  /** To change attributes of video tag go to '/src/config/quill-blots' "VideoBlot" */
  const videoHandler = useCallback(() => {
    const input = document.createElement('input');

    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/*');
    input.click();

    input.addEventListener('change', async () => {
      if (input.files && quillRef.current) {
        const file = input.files[0] as RcFile;
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);

        try {
          const response = await videoUploader.fetchFile(file).finally(() => setPercentCompleted(null));
          const url = response?.location || '';

          editor.insertEmbed(range.index, 'video', url);
          editor.setSelection(range.index + 1, 0);
        } catch (error) {
          console.error('Video upload failed:', error);
        }
      }
    });
  }, [videoUploader]);

  /** Step 1 of initialization. Define modules WITHOUT the handlers object. */
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          // [{ size: ['small', false, 'large', 'huge'] }], // 👈 this adds the dropdown
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike', 'blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['table'],
          allowMediaUpload ? ['link', 'image', 'video'] : [],
          ['clean'],
          ['fullscreen'],
        ],
        handlers: {
          table: insertArticleTable,
          /** Must be registered here: Quill ignores toolbar buttons that have no handler at init. */
          fullscreen: () => {
            const wrapper = quillRef.current?.getEditor().root
              .closest('.rich-text-editor-wrapper') as HTMLElement | null;

            if (wrapper && !wrapper.closest('.rich-text-editor-fullscreen')) {
              setLayoutMaxWidth(getLayoutMaxWidth(wrapper));
            }

            setIsFullscreen((prev) => !prev);
          },
        },
      },
      articleTable: true,
      clipboard: { matchVisual: false },
      blotFormatter: {
        overlay: {
          style: {
            border: '2px solid #00BFFF',
          },
        },
      },
    }),
    [] /** Modules should be static, because in other case editor will fail to build */,
  );

  /** Step 2: Use useEffect to attach handlers imperatively. */
  useEffect(() => {
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const toolbar = editor.getModule('toolbar');
    const tableButton = toolbar.container?.querySelector('button.ql-table') as HTMLButtonElement | null;

    if (tableButton && !tableButton.querySelector('svg')) {
      tableButton.innerHTML = TABLE_ICON;
    }

    tableButton?.setAttribute('title', 'Insert table');

    if (allowMediaUpload) {
      toolbar.addHandler('image', imageHandler);
      toolbar.addHandler('video', videoHandler);
    }
  }, [imageHandler, videoHandler, allowMediaUpload]);

  /** Fullscreen: swap the button icon, lock page scroll, close on Escape. */
  useEffect(() => {
    if (!quillRef.current) return undefined;

    const toolbar = quillRef.current.getEditor().getModule('toolbar');
    const fullscreenButton = toolbar.container?.querySelector('button.ql-fullscreen') as HTMLButtonElement | null;

    if (fullscreenButton) {
      fullscreenButton.innerHTML = isFullscreen ? FULLSCREEN_EXIT_ICON : FULLSCREEN_ENTER_ICON;
      fullscreenButton.setAttribute('title', isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen');
    }

    if (!isFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  return (
    <Spin
      tip={(
        <div className="progress-container">
          {percentCompleted ? <Progress percent={percentCompleted} /> : null}
          File uploading...
        </div>
      )}
      spinning={imageUploader.loading || videoUploader.loading}
      wrapperClassName={clsx({ 'rich-text-editor-fullscreen': isFullscreen })}
    >
      <Form.Item
        label={label}
        name="description"
        initialValue=""
        {...props}
        style={{
          ...props.style,
          ...(isFullscreen && layoutMaxWidth ? { maxWidth: layoutMaxWidth } : {}),
        }}
        rules={[...defaultRules, ...rules]}
        className={clsx('rich-text-editor-wrapper', { 'root-disabled': disabled })}
      >
        <ReactQuill ref={quillRef} theme="snow" modules={modules} readOnly={disabled} {...inputProps} />
      </Form.Item>
    </Spin>
  );
}

export default InputRichDescription;
